import React from "react";
import { Box, Typography, Chip, Stack, Divider, Tooltip } from "@mui/material";
import { TrendingUp, TrendingDown, Remove } from "@mui/icons-material";
import { getTrendColor } from "../utils/comparisonUtils";

const ComparisonDisplay = ({ data, type = "exercise" }) => {
  if (!data || !data.hasData) return null;

  const renderTrendIcon = (trend) => {
    const iconProps = {
      fontSize: "small",
      sx: { mr: 0.5 },
    };

    switch (trend) {
      case "positive":
        return <TrendingUp {...iconProps} />;
      case "negative":
        return <TrendingDown {...iconProps} />;
      default:
        return <Remove {...iconProps} />;
    }
  };

  const renderComparisonChip = (comparison, label) => {
    if (!comparison) return null;

    const color = getTrendColor(comparison.trend);
    const displayText =
      comparison.display || `${comparison.current} vs ${comparison.previous}`;

    return (
      <Tooltip
        title={`${label}: ${comparison.current} (was ${comparison.previous})`}
        arrow
      >
        <Chip
          icon={renderTrendIcon(comparison.trend)}
          label={`${label}: ${displayText}`}
          size="small"
          color={color}
          variant="outlined"
          sx={{
            fontSize: "0.75rem",
            height: "auto",
            "& .MuiChip-label": {
              px: 1,
              py: 0.5,
            },
          }}
        />
      </Tooltip>
    );
  };

  const renderExerciseComparison = () => {
    return (
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
          {data.weightComparison &&
            renderComparisonChip(data.weightComparison, "Avg Weight")}
          {data.exerciseCompletion &&
            renderComparisonChip(data.exerciseCompletion, "Exercises")}
          {data.setCompletion &&
            renderComparisonChip(data.setCompletion, "Sets")}
        </Stack>

        {data.summary && data.summary.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Box>
              {data.summary.map((insight, index) => (
                <Typography
                  key={index}
                  variant="caption"
                  color={
                    insight.type === "positive" ? "success.main" : "error.main"
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 0.5,
                    fontSize: "0.75rem",
                  }}
                >
                  {renderTrendIcon(insight.type)}
                  {insight.message}
                </Typography>
              ))}
            </Box>
          </>
        )}
      </Stack>
    );
  };

  const renderGenericComparison = () => {
    // For future extension - nutrition, compliance, etc.
    return (
      <Typography variant="body2" color="text.secondary">
        Comparison data available for analysis
      </Typography>
    );
  };

  return (
    <Box
      sx={{
        mt: 2,
        pt: 2,
        borderTop: "1px solid",
        borderColor: "divider",
        backgroundColor: "action.hover",
        borderRadius: 1,
        p: 2,
      }}
    >
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{
          mb: 1.5,
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
        }}
      >
        📊 vs. Previous Report
      </Typography>

      {type === "exercise"
        ? renderExerciseComparison()
        : renderGenericComparison()}
    </Box>
  );
};

export default ComparisonDisplay;
